import { PayloadAction } from "@reduxjs/toolkit";
import { BeerProposalType, ProposalReduxState } from "../types";
import {
  DiscountType,
  PackagedBeerDetailsContainer,
  RecallType,
} from "../../../../gql/hooks";
import { generateShortId } from "../../../../utils/library/shortId";

export const getDefaultBeerInfo = {
  packagedProductUnitNb: "",
  freePackagedProductUnitsNb: "",
  discountType: DiscountType.Percentage,
  recallType: RecallType.LiterRecall,
  amortizationType: RecallType.LiterRecall,
};

export type AddProductsToProposalType = {
  productId: number;
  container?: PackagedBeerDetailsContainer;
} & Partial<BeerProposalType>;

const AddProductsToProposal = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{ products: AddProductsToProposalType[]; reset?: boolean }>,
) => {
  const { products, reset } = payload;
  const baseOrder = reset ? 0 : state.products?.length || 0;

  const newProducts = products
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(({ productId, container, ...product }, index) => ({
      ...getDefaultBeerInfo,
      ...(product || {}),
      id: generateShortId(productId),
      order: baseOrder + index,
      productId,
      container,
    }));

  state.products = reset ? newProducts : [...state.products, ...newProducts];
  state.isUpdated = true;
};

const UpdateProposalProduct = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{
    id: string;
    product: Partial<BeerProposalType>;
  }>,
) => {
  const { id, product } = payload;

  const productIndex = state.products.findIndex((product) => product.id === id);
  if (productIndex < 0) return;

  state.products[productIndex] = {
    ...state.products[productIndex],
    ...product,
  };
  state.isUpdated = true;
};

const UpdateProposalProductOrder = (
  state: ProposalReduxState,
  { payload: { id, order } }: PayloadAction<{ id: string; order: number }>,
) => {
  const currentIndex = state.products.findIndex((item) => item.id === id);

  if (currentIndex > -1) {
    const updated = [...state.products];
    const [item] = updated.splice(currentIndex, 1);
    updated.splice(order, 0, item);
    state.products = updated.map((product, index) => ({
      ...product,
      order: index,
    }));
  }
};

const RemoveProductsFromProposal = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{
    ids: string[];
  }>,
) => {
  state.products = state.products
    .filter((product) => !payload.ids.includes(product.id))
    .map((product, index) => ({ ...product, order: index }));
  state.isUpdated = true;
};

const RemoveProductsFromProposalByProduct = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{
    products: AddProductsToProposalType[];
  }>,
) => {
  state.products = state.products
    .reduce((acc, item) => {
      const foundProduct = payload.products.find(
        (product) =>
          product.productId === item.productId &&
          product.container === item.container,
      );
      if (!foundProduct) {
        acc.push(item);
      }
      return acc;
    }, [] as BeerProposalType[])
    .map((product, index) => ({ ...product, order: index }));
  state.isUpdated = true;
};

// Update full Products list
const UpdateProposalProducts = (
  state: ProposalReduxState,
  {
    payload,
  }: PayloadAction<{
    products: BeerProposalType[];
  }>,
) => {
  const { products } = payload;
  state.products = products;
  state.isUpdated = true;
};

const ProductReducers = {
  // Products Specific
  AddProductsToProposal,
  UpdateProposalProduct,
  UpdateProposalProductOrder,
  RemoveProductsFromProposal,
  RemoveProductsFromProposalByProduct,

  // Products General
  UpdateProposalProducts,
};

export default ProductReducers;
